<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { isAutocompletePrediction } from '~/lib/interfaces/helpers'
import {
  RestaurantAreas,
  RestaurantLiteWithDistance,
  RestaurantLiteWithDistanceList,
} from '~/lib/interfaces/restaurant'

const props = defineProps<{
  restaurantList: RestaurantLiteWithDistanceList
  smallCard?: boolean
  citySlug?: string
}>()

const restaurantStore = useRestaurantStore()
const { selectedGoogleLocation } = storeToRefs(restaurantStore)

const trigger = ref<HTMLElement>()

const emit = defineEmits(['after-select-restaurant'])
const restaurantActions = useRestaurantActions()
const { trackSelectRestaurant } = useGTM()
const { t } = useLocale()

// Order of sort: 1. isFavorite, 2. hasEcormmerce, 3. distance. IsFavorite and ecommerce are boolean, and distance is a number
const getRestaurantList = () => {
  if (!props.restaurantList) return []
  const sorted = [...props.restaurantList].sort((a, b) => {
    return (
      Number(b.isFavorite) - Number(a.isFavorite) ||
      Number(b.ecommerce) - Number(a.ecommerce) ||
      a.distance! - b.distance!
    )
  })

  const autoCompleteTerms =
    selectedGoogleLocation.value && isAutocompletePrediction(selectedGoogleLocation.value, 'terms')
      ? selectedGoogleLocation.value.terms.map(({ value }) => slugify(value))
      : undefined

  return sorted.filter(res => {
    // Only sort by autocomplete terms when citySlug
    if (props.citySlug && autoCompleteTerms) {
      if (!res.city) return false
      return (
        autoCompleteTerms.includes(slugify(res.city)) ||
        (res.neighborhood && autoCompleteTerms.includes(slugify(res.neighborhood)))
      )
    }
    return true
  })
}

const sortedRestaurantList = ref(getRestaurantList())

const restaurants = useInfiniteScroll({
  data: sortedRestaurantList,
  itemsPerPage: 10,
  domRef: trigger,
})

watch(selectedGoogleLocation, () => {
  sortedRestaurantList.value = getRestaurantList()
})

watch(
  () => props.restaurantList.length,
  () => {
    sortedRestaurantList.value = getRestaurantList()
  }
)

const selectRestaurant = async (restaurant: RestaurantLiteWithDistance) => {
  trackSelectRestaurant(restaurant.code)
  await restaurantStore.getRestaurantByCode({
    code: restaurant.code,
    area: RestaurantAreas.MOP,
  })
  await restaurantActions.setRestaurant(restaurantStore.restaurant!)
  emit('after-select-restaurant')
}
</script>

<template>
  <div class="relative flex flex-col gap-4 text-left">
    <template v-if="!restaurants.length">
      <p class="p-4 text-sm font-bold md:px-0">
        {{ t('selectRestaurant.MOP.emptyList') }}
      </p>
    </template>

    <template v-else>
      <template v-for="restaurant in restaurants" :key="restaurant.id">
        <RestaurantListItem
          :restaurant="restaurant"
          :small-card="smallCard"
          @select-restaurant="selectRestaurant"
        />
      </template>
      <span ref="trigger" class="absolute bottom-[100px] left-0"></span>
    </template>
  </div>
</template>
